<template>
  <div>
    <dl class="tabulatedList">
      <!-- Nom de l'entreprise -->
      <dt>
        <span><span v-html="$t('views.synthesisView.compagnySummary.compagny')"></span></span>
      </dt>
      <dd>{{ gpsst.compagnyContext.name }}</dd>

      <!-- Unité de travail -->
      <dt>
        <span><span v-html="$t('views.synthesisView.compagnySummary.unitName')"></span></span>
      </dt>
      <dd>{{ gpsst.compagnyContext.unitName }}</dd>

      <!-- Parties prenantes -->
      <template v-if="gpsst.compagnyContext.stakeholders.length > 0">
        <dt>
          <span><span v-html="$t('views.synthesisView.compagnySummary.stakeholders')"></span></span>
        </dt>
        <dd>
          <ul class="stakeholders">
            <li
              class="my-p"
              v-for="(stakeholder, index) in gpsst.compagnyContext.stakeholders"
              :key="index"
            >
              <span class="sh-name" v-if="stakeholder.name">{{
                stakeholder.name
              }}</span>
              <span class="sh-function" v-if="stakeholder.function">{{
                stakeholder.function
              }}</span>
              <span
                class="sh-involvementMode"
                v-if="stakeholder.involvementMode"
                >{{ stakeholder.involvementMode }}</span
              >
            </li>
          </ul>
        </dd>
      </template>

      <!-- Date d'analyse en cours -->
      <dt>
        <span><span v-html="$t('views.synthesisView.compagnySummary.currentAchievementDate')"></span></span>
      </dt>
      <dd>
        {{ gpsst.compagnyContext.completionPeriods.currentDate }}
      </dd>

      <!-- Date d'analyse précédente -->
      <template v-if="gpsst.compagnyContext.completionPeriods.previousDate">
        <dt>
          <span><span v-html="$t('views.synthesisView.compagnySummary.lastAchievementDate')"></span></span>
        </dt>
        <dd>
          {{ gpsst.compagnyContext.completionPeriods.previousDate }}
        </dd>
      </template>

      <!-- Commentaires sur l'organisation du travail d'évaluation -->
      <template v-if="gpsst.compagnyContext.evaluationWorkComment">
        <dt>
          <span><span v-html="$t('views.synthesisView.compagnySummary.evaluationWorkComment')"></span></span>
        </dt>
        <dd style="white-space: pre-line;">{{ gpsst.compagnyContext.evaluationWorkComment }}</dd>
      </template>
    </dl>



    <div class="section">
      <h4 class="myOrangeTitle" v-html="$t('views.synthesisView.title1')"></h4>
      <div class="resultsAndGraph">
        <div class="results">
          <table
            v-if="sortedThemes.completedThemes.length > 0"
            class="themes-table"
          >
            <thead>
              <tr>
                <th>{{ $t("views.synthesisView.tableColumns.abbr") }}</th>
                <th>{{ $t("views.synthesisView.tableColumns.theme") }}</th>
                <th colspan="2">
                  {{ $t("views.synthesisView.tableColumns.globalRating") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                :class="'theme-line slice' + Math.floor((theme.globalRating.rating - 0.0001) / 25)"
                v-for="theme in sortedThemes.completedThemes"
                :key="theme.abberviation"
                @click="$emit('changeview', { view: 'theme', theme: theme })"
              >
                <td>{{ theme.abbreviation }}</td>
                <td>{{ theme.title }}</td>
                <td>
                  <span class="sliceAndRating">
                    {{ getSliceAndRatingLabel(theme.globalRating.rating) }}
                  </span>
                </td>
                <td>
                  <span class="progress">
                    <span 
                      class="bar"
                      :style="{ width: theme.globalRating.rating + '%' }"
                    ></span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <span
            v-if="sortedThemes.notCompletedThemes.length"
            class="warning-msg"
          >
            <alert-icon title="" />
            <p
              class="my-p"
              v-html="
                $t('views.synthesisView.msgThemesNotCompleted.someIncomplete')
              "
            ></p>
          </span>

          <table
            v-if="sortedThemes.notCompletedThemes.length"
            class="themes-table"
          >
            <thead>
              <tr>
                <th>{{ $t("views.synthesisView.tableColumns.abbr") }}</th>
                <th>{{ $t("views.synthesisView.tableColumns.theme") }}</th>
                <th>{{ $t("views.synthesisView.tableColumns.status") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="theme-line"
                v-for="theme in sortedThemes.notCompletedThemes"
                :key="theme.abberviation"
                @click="$emit('changeview', { view: 'theme', theme: theme })"
              >
                <td>{{ theme.abbreviation }}</td>
                <td>{{ theme.title }}</td>
                <td
                  v-html="$t('views.synthesisView.status.' + theme.status)"
                ></td>
              </tr>
            </tbody>
          </table>
        </div>

        <spider-graph
          class="spiderGraph"
          :themes="sortedThemes.completedThemes"
        ></spider-graph>
      </div>
    </div>

    <div class="section">
      <h4 class="myOrangeTitle" v-html="$t('views.synthesisView.title2')"></h4>

      <p
        class="progressproposals-container"
        v-if="sortedThemes.completedThemes.length == 0"
        v-html="$t('views.synthesisView.msgThemesNotCompleted.allIncomplete')"
      ></p>

      <div
        class="progressProposals-container"
        v-for="theme in sortedThemes.completedThemes"
        :key="theme.abbreviation"
      >
        <h5 class="theme-title">
          <strong>{{ theme.abbreviation }}</strong> - {{ theme.title }}
        </h5>
        <progress-proposals
          :edit="theme.edit"
          :theme="theme"
          :printMode="printMode"
          view="synthesis"
          v-on:endEdit="theme.edit = false"
        ></progress-proposals>
      </div>
    </div>
  </div>
</template>

<script>
import SpiderGraph from "./SpiderGraph";
import ProgressProposals from "../theme/ProgressProposals";
import AlertIcon from "vue-material-design-icons/Alert.vue";

export default {
  name: "Synthesis",
  components: {
    SpiderGraph,
    ProgressProposals,
    AlertIcon
  },
  props: {
    printMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    gpsst: function() {
      return this.$store.state.gpsstModule.gpsst;
    },
    sortedThemes: function() {
      return this.sortThemes();
    }
  },
  methods: {
    getSliceAndRatingLabel: function(rating) {
      var sliceIndex = Math.min(Math.floor(rating / 25), 3);
      var sliceLabel = this.$store.state.gpsstModule.gpsst.slices[sliceIndex].title;
      return this.$i18n.t("views.synthesisView.themeRatingLabel", {"rating": rating, "sliceLabel": sliceLabel});
    },
    sortThemes: function() {
      //TODO à factoriser avec celui de PopinPrint.vue

      var completedThemes = [];
      var notCompletedThemes = [];
      var themes = this.$store.state.gpsstModule.gpsst.themes;

      for (let h = 0; h < themes.length; h++) {
        var theme = themes[h];
        if (theme.status == "complete") {
          // permet de créer l'objet qui indique au tooltip de s'afficher selon le click de l'utilisateur
          // on utilise this.$set car cela permet de rendre réactif l'ajout de la propriété, afin que tooltip puisse voir les modifications affectées à celle-ci
          this.$set(theme, "tooltip", false);
          this.$set(theme, "edit", false);
          completedThemes.push(theme);
        } else {
          this.$set(theme, "tooltip", false);
          this.$set(theme, "edit", false);
          notCompletedThemes.push(theme);
        }
      }

      // on trie les thèmes ayant une cotation par cotation croissante et à cotation égale, par ordre alphabétique
      completedThemes.sort(function(a, b) {
        if (a.globalRating.rating != b.globalRating.rating) {
          return a.globalRating.rating - b.globalRating.rating;
        }
        return b.title.localeCompare(a.title);
      });

      // on trie les autres thèmes par ordre alphabétique
      notCompletedThemes.sort(function(a, b) {
        return b.title.localeCompare(a.title);
      });

      return {
        completedThemes: completedThemes,
        notCompletedThemes: notCompletedThemes
      };
    }
  }
};
</script>

<style lang="sass" scoped>
@import "../../sass/_variables.sass"
@import "../../sass/_media.sass"

.section
  break-inside: avoid  
  page-break-inside: avoid

.line
  width: 100%
  position: absolute
  top: 0

.tabulatedList
  display: block
  margin-top: 2em
  margin-left: $synthesisView-leftMargin

  dt
    display: block
    width: 30%
    margin-bottom: 0.2em

    > span
      position: relative
      display: block
      width: 100%
      height: auto
      padding-right: 1em

      &:before
        content: " "
        position: absolute
        bottom: 0.25em
        width: 100%
        z-index: -1
        border-bottom: 1px solid $lightgray-2

      > span
        font-weight: bold
        background-color: white
        padding: 0 0.2em 0 0


  dd
    display: block
    width: 70%
    margin: -1.4em 0 0.2em 30%
    padding: 0 0 0 0.4em

    ul
      margin: 0
      padding: 0 1.3em
      list-style: square

ul.stakeholders
  li
    margin: 0 0 0.5em 0
    &:last-child
      margin-bottom: 0

    .sh-name ~ .sh-function
      &:before
        content: " : "

    .sh-involvementMode
      display: block
      white-space: pre-line
      color: $darkgray-3
      font-size: 0.9em


.my-p
  margin: 0

.progress
  border-radius: 0

  .bar
    height: 1.2em

    .slice0 &
      background-color: $slice-backgroundcolor-1      
    .slice1 &
      background-color: $slice-backgroundcolor-2
    .slice2 &
      background-color: $slice-backgroundcolor-3 
    .slice3 &
      background-color: $slice-backgroundcolor-4


.resultsAndGraph
  display: flex
  flex-wrap: wrap
  justify-content: flex-start
  align-content: stretch

  .results
    flex-grow: 4
    width: 60%
    margin-left: $synthesisView-leftMargin
    
    @include md 
      width: 100%

    .themes-table
      width: 100%
      margin: 0 0 2em 0  
      thead
        tr
          th
            color: white
            background-color: $darkgray-1
            text-transform: uppercase
            padding: 0.3em 0.6em
            white-space: nowrap

      tbody
        tr
          cursor: pointer

          //&.slice0
          //  color: scale-color($slice-backgroundcolor-1, $lightness: -0%)
          //&.slice1
          //  color: scale-color($slice-backgroundcolor-2, $lightness: -0%)
          //&.slice2
          //  color: scale-color($slice-backgroundcolor-3, $lightness: -0%)
          //&.slice3
          //  color: scale-color($slice-backgroundcolor-4, $lightness: -0%)

          td
            vertical-align: top
            padding: 0.5em 0.6em

            &:nth-child(1)
              //width: 15%
              white-space: nowrap

            &:nth-child(2)
              //width: 50%

            &:nth-child(3)
              //width: 15%
              white-space: nowrap

            &:nth-child(4)
              //width: 20%
              width: 15%

              .progress
                font-size: 1em
                height: 1.2em

    .warning-msg
      margin: 2em 0 0.5em 0

  .spiderGraph
    flex-grow: 1
    margin-left: 3em

    @include md 
      float: none
      width: 100%
      margin-left: 0

.progress-proposals
  background-color:$lightgray-3

.progressProposals-container
  margin-right: $synthesisView-leftMargin
  margin-left: $synthesisView-leftMargin
  margin-top: 1.5em
  break-inside: avoid
  page-break-inside: avoid

  &:first-of-type
    margin-top: 0.5em

  .theme-title
    color: $darkgray-3

</style>

